import { FileText } from "lucide-react";
import { useEffect, useRef } from "react";

import { HorizontalFlexbox } from "@src/shared/components/layout/Flexbox";
import { cn } from "@src/shared/lib/utils";
import { formatDurationMmSs } from "@src/shared/utils/dates";

const SCROLL_BOTTOM_THRESHOLD_PX = 48;

export interface VisitTranscriptItemV2 {
  id: string;
  startOffsetMs: number;
  endOffsetMs?: number;
  speaker?: string | null;
  speakerLabel?: string | null;
  state?: "not_sent" | "sent" | "processed" | "errored";
  text?: string | null;
}

export interface VisitTranscriptProps {
  items: VisitTranscriptItemV2[];
}

/**
 * Chromeless transcript body: just the scrollable line list (or the empty
 * state). `AgendaDock` owns the toggle/header chrome and the live line count
 * shown there — this component only renders the content.
 *
 * Stays mounted (visually hidden via `visibility`) for the whole visit once
 * a transcript exists, not just while its dock section is expanded — see
 * `AgendaDock`'s `transcript` prop doc and `DockSection`'s doc comment for
 * why (the section's height needs to animate open over already-laid-out
 * content). That means this component re-renders on every transcript poll
 * tick and its scroll-tracking effects (below) run even while collapsed;
 * unlike the scratch pad there's no in-flight save to protect here, but the
 * per-tick cost of those effects is a couple of DOM layout-property reads
 * gated by `items.length`, not worth threading an open/closed prop down to
 * avoid.
 */
export function VisitTranscript({ items }: VisitTranscriptProps) {
  const listRef = useRef<HTMLOListElement>(null);
  const isAtBottomRef = useRef(true);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    isAtBottomRef.current =
      scrollHeight - scrollTop - clientHeight <= SCROLL_BOTTOM_THRESHOLD_PX;
  };

  // Keep the live transcript pinned to the latest line unless the user
  // scrolled up. This covers the common case: new lines grow `scrollHeight`
  // without changing the list's own (flex-constrained) box size, so a
  // ResizeObserver on the list wouldn't fire for it.
  useEffect(() => {
    if (!isAtBottomRef.current) return;
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight - el.clientHeight;
  }, [items.length]);

  // Re-pin to bottom when the list's own box goes from zero size to real
  // size — e.g. right after this panel is expanded from the dock. Complements
  // (does not replace) the effect above.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (!isAtBottomRef.current) return;
      el.scrollTop = el.scrollHeight - el.clientHeight;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (items.length === 0) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        className="gap-2 rounded-[9px] border border-dashed border-border px-3 py-4 text-[12px] text-muted-foreground"
      >
        <FileText className="size-4" aria-hidden="true" />
        No transcript yet. Start recording to capture the visit.
      </HorizontalFlexbox>
    );
  }

  return (
    <ol
      ref={listRef}
      onScroll={handleScroll}
      className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto pb-2 pr-2"
      aria-label="Transcript items"
    >
      {items.map((item) => (
        <TranscriptRow key={item.id} item={item} />
      ))}
    </ol>
  );
}

function TranscriptRow({ item }: { item: VisitTranscriptItemV2 }) {
  const isPending = item.state === "not_sent" || item.state === "sent";
  const isErrored = item.state === "errored";

  return (
    <li className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[12px] leading-relaxed">
      <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
        {formatDurationMmSs(item.startOffsetMs)}
      </span>
      <p
        className={cn(
          "min-w-0 text-foreground",
          isPending && "italic text-muted-foreground",
          isErrored && "text-destructive",
        )}
      >
        {item.speakerLabel ? (
          <span className="font-semibold text-primary">
            {item.speakerLabel}:{" "}
          </span>
        ) : null}
        {item.text?.trim() ? item.text : "(no transcription)"}
      </p>
    </li>
  );
}
